// ==================================
// File: src/pages/TermsPage.jsx
// AdSense-friendly, full Terms & Conditions
// ==================================
import { Box, Container, Typography, Paper } from "@mui/material";
import Navbar from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Helmet } from "react-helmet";
import PageContainer from "../components/PageContainer";
export default function TermsPage() {
  return (
    <PageContainer>
    <Box sx={{ bgcolor: "#f7f9fc", minHeight: "100vh" }}>
      {/* ==================== CLEAN META ONLY (NO CHAT WIDGETS) ===================== */}
      <Helmet>
        <title>Terms &amp; Conditions - CBT Nova</title>
        <meta
          name="description"
          content="Read the terms and conditions governing the use of CBT Nova, an online CBT practice platform for WAEC, NECO, JAMB and school exams."
        />
      </Helmet>

      <Navbar />

      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3 }}>
          <Typography variant="h4" fontWeight={800} gutterBottom>
            Terms &amp; Conditions
          </Typography>

          <Typography variant="body1" sx={{ mt: 3, lineHeight: 1.85 }}>
            These Terms &amp; Conditions (“Terms”) govern your access to and
            use of CBT Nova (“we”, “our”, or “us”) and all related services,
            including practice exams, analytics and account features. By
            accessing or using CBT Nova, you agree to be bound by these Terms.
            If you do not agree, you must stop using the platform immediately.
          </Typography>

          {/* 1. ELIGIBILITY */}
          <Typography variant="h5" fontWeight={700} sx={{ mt: 4 }}>
            1. Eligibility
          </Typography>
          <Typography sx={{ mt: 1.5, lineHeight: 1.85 }}>
            You may use CBT Nova only if you have the legal capacity to enter
            into a binding agreement under applicable law, or if you are using
            the platform under the supervision and consent of a parent, guardian
            or school representative.
          </Typography>

          {/* 2. USER ACCOUNTS */}
          <Typography variant="h5" fontWeight={700} sx={{ mt: 4 }}>
            2. User Accounts
          </Typography>
          <Typography sx={{ mt: 1.5, lineHeight: 1.85 }}>
            You are responsible for maintaining the confidentiality of your
            login details and for all activities that occur under your account.
            You agree to:
            <br />
            • Provide accurate and up-to-date information during registration{" "}
            <br />
            • Notify us promptly of any unauthorized access or security breach{" "}
            <br />
            CBT Nova is not liable for any loss or damage arising from your
            failure to protect your account credentials.
          </Typography>

          {/* 3. USE OF MATERIALS */}
          <Typography variant="h5" fontWeight={700} sx={{ mt: 4 }}>
            3. Use of Content and Materials
          </Typography>
          <Typography sx={{ mt: 1.5, lineHeight: 1.85 }}>
            All questions, explanations, exam interfaces, graphics, code and
            other content on the platform are owned by CBT Nova or its
            licensors and are protected by copyright and other intellectual
            property laws. You are granted a limited, non-exclusive,
            non-transferable license to use the platform for personal study
            purposes only. You may not copy, reproduce, distribute, modify,
            resell or publicly display any part of the platform without our
            prior written consent.
          </Typography>

          {/* 4. PROHIBITED ACTIVITIES */}
          <Typography variant="h5" fontWeight={700} sx={{ mt: 4 }}>
            4. Prohibited Activities
          </Typography>
          <Typography sx={{ mt: 1.5, lineHeight: 1.85 }}>
            You agree not to:
            <br />
            • Attempt to hack, bypass or interfere with the platform&apos;s
            security or scoring systems <br />
            • Share your account with others or allow unauthorized access{" "}
            <br />
            • Use bots, scripts or automated tools to take tests or scrape data{" "}
            <br />
            • Upload or transmit any harmful, abusive, illegal or infringing
            content <br />
            • Use CBT Nova in any manner that could damage, disable or impair
            the service or its infrastructure
          </Typography>

          {/* 5. SUBSCRIPTIONS & PAYMENTS */}
          <Typography variant="h5" fontWeight={700} sx={{ mt: 4 }}>
            5. Subscriptions and Payments
          </Typography>
          <Typography sx={{ mt: 1.5, lineHeight: 1.85 }}>
            If CBT Nova offers paid plans or subscriptions, you agree to pay
            all applicable fees as displayed at the point of purchase. Payment
            processing may be handled by third-party providers subject to their
            own terms and privacy policies. Access to premium features may be
            suspended or terminated if payment is not completed or reversed.
          </Typography>

          {/* 6. REFUNDS */}
          <Typography variant="h5" fontWeight={700} sx={{ mt: 4 }}>
            6. Refund Policy
          </Typography>
          <Typography sx={{ mt: 1.5, lineHeight: 1.85 }}>
            Where applicable, refund requests will be considered in line with
            our refund policy as published on the platform. Certain fees may be
            non-refundable except where required by law.
          </Typography>

          {/* 7. DISCLAIMER */}
          <Typography variant="h5" fontWeight={700} sx={{ mt: 4 }}>
            7. Disclaimer of Warranties
          </Typography>
          <Typography sx={{ mt: 1.5, lineHeight: 1.85 }}>
            CBT Nova provides examination practice resources “as is” and “as
            available” without any warranties, express or implied. We do not
            guarantee that:
            <br />
            • Your exam results will improve or that you will pass any exam{" "}
            <br />
            • The platform will be error-free, uninterrupted or always
            available <br />
            • All questions will exactly match future exam questions
          </Typography>

          {/* 8. LIMITATION OF LIABILITY */}
          <Typography variant="h5" fontWeight={700} sx={{ mt: 4 }}>
            8. Limitation of Liability
          </Typography>
          <Typography sx={{ mt: 1.5, lineHeight: 1.85 }}>
            To the maximum extent permitted by law, CBT Nova and its officers,
            employees and partners shall not be liable for any indirect,
            incidental, consequential or punitive damages arising from your use
            of, or inability to use, the platform.
          </Typography>

          {/* 9. TERMINATION */}
          <Typography variant="h5" fontWeight={700} sx={{ mt: 4 }}>
            9. Suspension or Termination
          </Typography>
          <Typography sx={{ mt: 1.5, lineHeight: 1.85 }}>
            We may suspend or terminate your access to CBT Nova at any time,
            with or without notice, if we believe you have violated these Terms
            or engaged in fraudulent or abusive behaviour. Upon termination, any
            rights granted to you under these Terms will immediately cease.
          </Typography>

          {/* 10. CHANGES TO TERMS */}
          <Typography variant="h5" fontWeight={700} sx={{ mt: 4 }}>
            10. Changes to These Terms
          </Typography>
          <Typography sx={{ mt: 1.5, lineHeight: 1.85 }}>
            We may update or revise these Terms from time to time. The updated
            version will be posted on this page with the effective date. Your
            continued use of the platform after changes are made constitutes
            acceptance of the revised Terms.
          </Typography>

          {/* 11. GOVERNING LAW */}
          <Typography variant="h5" fontWeight={700} sx={{ mt: 4 }}>
            11. Governing Law
          </Typography>
          <Typography sx={{ mt: 1.5, lineHeight: 1.85 }}>
            These Terms shall be governed by and construed in accordance with
            the laws of the Federal Republic of Nigeria, without regard to its
            conflict of law provisions.
          </Typography>



{/* 8. FAIR USE & ENFORCEMENT */}
<Typography variant="h5" fontWeight={1200} sx={{ mt: 4 }}>
SUBSCRIPTIONS & PAYMENTS
</Typography>




{/* 5. SUBSCRIPTIONS & ACCESS */}
<Typography variant="h5" fontWeight={700} sx={{ mt: 4 }}>
  1. Subscriptions and Access
</Typography>
<Typography sx={{ mt: 1.5, lineHeight: 1.85 }}>
  CBT Nova offers paid subscription plans that grant access to premium
  features, including full practice exams, analytics, and advanced learning
  tools. By subscribing, you acknowledge and agree that:
  <br /><br />
  • Only one active subscription is permitted per user at any time <br />
  • Subscriptions activate immediately after successful payment confirmation <br />
  • Access is linked strictly to the subscribed account and may not be shared,
    transferred, or resold <br />
  • Subscription access expires automatically at the end of the plan duration
</Typography>

{/* 6. PLAN CHANGES & SWITCHING */}
<Typography variant="h5" fontWeight={700} sx={{ mt: 4 }}>
  2. Plan Changes, Switching & Cancellation
</Typography>
<Typography sx={{ mt: 1.5, lineHeight: 1.85 }}>
  Users may subscribe to a new plan at any time. When a new subscription is
  activated:
  <br /><br />
  • Any existing active subscription is automatically ended <br />
  • The new plan becomes effective immediately <br />
  • Remaining time on a previous plan does not carry over
  <br /><br />
  Subscriptions cannot be paused or temporarily suspended. Users do not need
  to cancel an existing plan before purchasing a new one, as the system
  automatically manages plan replacement.
</Typography>

{/* 7. PAYMENTS & REFUND POLICY */}
<Typography variant="h5" fontWeight={700} sx={{ mt: 4 }}>
  3. Payments and Refund Policy
</Typography>
<Typography sx={{ mt: 1.5, lineHeight: 1.85 }}>
  All payments made on CBT Nova are final and non-refundable, except where
  required by applicable law. By completing a payment, you agree that:
  <br /><br />
  • Fees paid grant immediate digital access to subscribed features <br />
  • Refunds will not be issued for unused time, accidental purchases,
    plan changes, or early termination <br />
  • Payment records are securely stored for transparency and auditing purposes
  <br /><br />
  CBT Nova is not responsible for payment issues arising from third-party
  payment providers.
</Typography>

{/* 8. FAIR USE & ENFORCEMENT */}
<Typography variant="h5" fontWeight={700} sx={{ mt: 4 }}>
  4. Fair Use, Abuse & Enforcement
</Typography>
<Typography sx={{ mt: 1.5, lineHeight: 1.85 }}>
  CBT Nova reserves the right to suspend, restrict, or terminate access
  without refund where a user is found to have violated examination integrity,
  engaged in fraudulent activity, attempted to bypass access controls, or
  abused platform resources. Such actions may be taken with or without prior
  notice, depending on severity.
</Typography>







          {/* CONTACT */}
          <Typography sx={{ mt: 5, lineHeight: 1.85 }}>
            For questions or concerns about these Terms &amp; Conditions,
            please contact:
            <br />
            <br />
            <strong>Email:</strong> support@cbt-master.com.ng
          </Typography>
        </Paper>
      </Container>

      <Footer />
    </Box>
	  </PageContainer>
  );
}
